'use client';

import { c as _c } from "react/compiler-runtime";
import type { PropsWithStyles } from '@morpho-org/ui-v6/types';
import type { SortDirection } from '@tanstack/react-table';
import { cn } from '@morpho-org/ui-v6/utils';
import { Icon, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { Arrow } from '@repo/ui/icons';
import { type JSX, memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { TableReward } from '../TableReward';
export interface SortableHeaderCellRewardProps extends PropsWithStyles {
  columnName?: string;
  colSpan: number;
  children: ReactNode;
  isSorted?: false | SortDirection;
  isSortable?: boolean;
  isPinned?: boolean;
  onClick: () => void;
}
export const SortableHeaderCellRootReward = t0 => {
  const $ = _c(6);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <TableReward.Th {...props}>{children}</TableReward.Th>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const SortableHeaderCellReward = t0 => {
  const $ = _c(18);
  const {
    columnName,
    colSpan,
    className,
    children,
    isSorted: t1,
    isSortable: t2,
    onClick
  } = t0;
  const isSorted = t1 === undefined ? false : t1;
  const isSortable = t2 === undefined ? true : t2;
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      if (!ref.current) {
        return;
      }
      const isHover = ref.current.contains(e.target as Node);
      setIsHovered(isHover);
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const handleGlobalMouseMove = t3;
  const debouncedMouseMove = useDebouncedCallback(handleGlobalMouseMove, 200);
  let t4;
  let t5;
  if ($[1] !== debouncedMouseMove) {
    t4 = () => {
      document.addEventListener("mousemove", debouncedMouseMove);
      return () => document.removeEventListener("mousemove", debouncedMouseMove);
    };
    t5 = [debouncedMouseMove];
    $[1] = debouncedMouseMove;
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  const tooltipContent = columnName ? `Sort by ${columnName}` : "Sort";
  let t6;
  if ($[4] !== className || $[5] !== isHovered || $[6] !== isSortable || $[7] !== isSorted || $[8] !== onClick || $[9] !== tooltipContent) {
    t6 = isSortable && <Tooltip><TooltipTrigger asChild={true}><div className={cn("flex items-center justify-center", cn("w-5 cursor-pointer hover:text-foreground hover:opacity-100", isSorted ? "text-secondary-foreground" : "text-muted-foreground/40", isSortable && (isHovered || Boolean(isSorted)) ? "opacity-100" : "opacity-0", className))} onClick={onClick}><Icon icon={<Arrow />} data-is-sorted={isSorted} className="[transform:rotate(90deg)_translateY(-0.5px)] data-[is-sorted=asc]:[transform:rotate(-90deg)]" /></div></TooltipTrigger><TooltipContent>{tooltipContent}</TooltipContent></Tooltip>;
    $[4] = className;
    $[5] = isHovered;
    $[6] = isSortable;
    $[7] = isSorted;
    $[8] = onClick;
    $[9] = tooltipContent;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== children || $[12] !== t6) {
    t7 = <div className="inline-flex items-center" ref={ref}>{children}{t6}</div>;
    $[11] = children;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== className || $[15] !== colSpan || $[16] !== t7) {
    t8 = <SortableHeaderCellRootReward className={className} colSpan={colSpan}>{t7}</SortableHeaderCellRootReward>;
    $[14] = className;
    $[15] = colSpan;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
export default memo(SortableHeaderCellReward);